import { useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, ArrowRight, Sparkles, Layers, Database, Gift, Settings, Brain, Building2, FileText, Smartphone, CreditCard, Shield, BarChart3, Zap, Target, Users } from 'lucide-react';
import { DemoModal } from '@/components/DemoModal';
import { ChatWidget } from '@/components/ChatWidget';
import MegaFooter from '@/components/MegaFooter';
import Navbar from '@/components/Navbar';
import SEOHead from '@/components/SEOHead';

const LOGO_URL = "/logo.svg";




const WhatsAppFloat = () => (
  <a href="https://wa.me/919910059861" target="_blank" rel="noopener noreferrer" className="whatsapp-float">
    <MessageCircle size={28} color="white" />
  </a>
);

// Feature Item Component
const FeatureItem = ({ icon: Icon, text, color, href }) => (
  <div className="flex items-start gap-3 p-3 bg-white rounded-xl border border-gray-100 shadow-sm">
    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${color}`}>
      <Icon size={16} className="text-white" />
    </div>
    {href ? (
      <a href={href} target="_blank" rel="noopener noreferrer" className="text-[#2C39B8] text-sm font-medium hover:underline">{text} ↗</a>
    ) : (
      <span className="text-gray-700 text-sm">{text}</span>
    )}
  </div>
);

// Band Card Component
const BandCard = ({ number, subtitle, title, description, features, capabilities, color, bgColor }) => (
  <div className="mb-12">
    <div className={`rounded-2xl overflow-hidden shadow-xl ${bgColor}`}>
      {/* Header */}
      <div className={`${color} px-6 py-4 flex items-center gap-4`}>
        <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
          <span className="text-2xl font-bold text-white">{number}</span>
        </div>
        <div>
          <div className="text-white/80 text-sm font-medium">{subtitle}</div>
          <h3 className="text-xl font-bold text-white">{title}</h3>
        </div>
      </div>
      
      {/* Content */}
      <div className="p-6">
        <p className="text-gray-600 mb-6">{description}</p>
        
        {/* Capabilities Grid */}
        <div className="grid md:grid-cols-2 gap-3 mb-6">
          {capabilities.map((cap, i) => (
            <FeatureItem key={i} icon={cap.icon} text={cap.text} color={color} href={cap.href} />
          ))}
        </div>
        
        {/* Feature Tags */}
        <div className="flex flex-wrap gap-2">
          {features.map((feature, i) => (
            <span key={i} className={`px-3 py-1.5 rounded-full text-xs font-medium ${color} text-white`}>
              {feature}
            </span>
          ))}
        </div>
      </div>
    </div>
  </div>
);

export default function PlatformPage() {
  const [demoModalOpen, setDemoModalOpen] = useState(false);

  const bands = [
    {
      number: "1",
      subtitle: "Foundational Layer",
      title: "B2B Channel Ecosystem Modeling",
      description: "Native modeling of real-world channel structures. ChannelLoyalty allows enterprises to digitally model their exact channel reality with hierarchical relationships, overlapping territories, role-based permissions, and distinct incentive logic.",
      color: "bg-[#2C39B8]",
      bgColor: "bg-blue-50",
      capabilities: [
        { icon: Building2, text: "Manufacturer / Brand HQ" },
        { icon: Users, text: "Regional Sales Offices & Super-Stockists" },
        { icon: Layers, text: "Distributors & Sub-Distributors" },
        { icon: Target, text: "Wholesalers, Dealers & Retailers" },
        { icon: Zap, text: "Influencers / Applicators / Contractors" },
      ],
      features: ["Multi-tier Hierarchy", "Role Permissions", "Territory Mapping", "Custom Attributes"]
    },
    {
      number: "2",
      subtitle: "Data Layer",
      title: "Engagement & Data Capture",
      description: "Capture the right data, at the right layer, at the right time. Each data point is: Role-mapped → Time-stamped → Validated → Scored for maximum intelligence.",
      color: "bg-green-600",
      bgColor: "bg-green-50",
      capabilities: [
        { icon: FileText, text: "Invoice / Bill Scan & OCR" },
        { icon: Database, text: "Sales Declarations & POS / ERP Feeds" },
        { icon: Smartphone, text: "WhatsApp Conversational Flows" },
        { icon: BarChart3, text: "Campaign Responses & Training" },
        { icon: Users, text: "Referrals & Advocacy Actions" },
      ],
      features: ["Bill Scan", "WhatsApp", "POS Integration", "Real-time Validation"]
    },
    {
      number: "3",
      subtitle: "Infrastructure",
      title: "Rewards, KYC & Payout Rails",
      description: "Enterprise-grade reward infrastructure, not just a catalog. Includes KYC verification, fraud detection, budget governance, and full audit trails.",
      color: "bg-cyan-500",
      bgColor: "bg-cyan-50",
      capabilities: [
        { icon: CreditCard, text: "Direct Cash / UPI / Bank Payouts" },
        { icon: Gift, text: "Digital Vouchers (Multi-brand, Multi-country)" },
        { icon: Layers, text: "Physical Goods with Tracked Logistics", href: "https://loyltykart.com" },
        { icon: Sparkles, text: "Travel & Experiential Rewards" },
        { icon: Shield, text: "Insurance & Education Products" },
      ],
      features: ["UPI Payouts", "Gift Vouchers", "KYC Verification", "Fraud Detection"]
    },
    {
      number: "4",
      subtitle: "Control Plane",
      title: "Admin, Rules & Automation Engine",
      description: "Design, launch & modify complex loyalty programs — without IT. Programs can be: Simulated → Launched → Modified → Rolled back — in minutes, not quarters.",
      color: "bg-orange-500",
      bgColor: "bg-orange-50",
      capabilities: [
        { icon: Settings, text: "Program Builder by Role & Geography" },
        { icon: Target, text: "Rule Engines: Slabs, Targets, Growth Incentives" },
        { icon: Shield, text: "Approval Workflows & Budget Caps" },
        { icon: Users, text: "Partner Lifecycle Management" },
        { icon: Layers, text: "Regional & Language Controls" },
      ],
      features: ["Rules Engine", "No-Code Builder", "Workflows", "Budget Controls"]
    },
    {
      number: "5",
      subtitle: "Intelligence",
      title: "ChannelLoyalty AI Brain",
      description: "From loyalty reporting to loyalty intelligence. Conversational AI: Ask 'Which distributors are declining despite incentives?' and get answers instantly.",
      color: "bg-gradient-to-r from-[#2C39B8] to-[#6FC8F5]",
      bgColor: "bg-gradient-to-br from-blue-50 to-blue-50",
      capabilities: [
        { icon: BarChart3, text: "Primary vs Secondary vs Tertiary Sales Attribution" },
        { icon: Target, text: "Influence Scoring Across Partners" },
        { icon: Shield, text: "Fraud & Anomaly Detection" },
        { icon: Zap, text: "Scheme ROI & Cost-per-Uplift" },
        { icon: Brain, text: "Prescriptive AI Recommendations" },
      ],
      features: ["Ask the Data", "Predictive AI", "ROI Analysis", "Natural Language"]
    }
  ];

  return (
    <div data-testid="platform-page">
      <Navbar onDemoClick={() => setDemoModalOpen(true)} />
      <SEOHead title="Platform Architecture - Multi-Tier Channel Loyalty Engine" description="ChannelLoyalty's enterprise platform architecture: QR scanning, invoice upload, ERP integration, AI analytics, instant UPI payouts, WhatsApp engagement. Built for 50,000+ partner networks." keywords="B2B loyalty platform architecture, channel loyalty technology, loyalty management software, multi-tier rewards engine" canonical="https://channelloyalty.ai/platform" />
      
      <main>
        {/* Banner Hero */}
        <section className="hero-banner-full">
          <div className="hero-banner-carousel">
            <div className="hero-banner-track">
              <div className="hero-banner-slide active">
                <img src="/images/banner_platform.jpg" alt="ChannelLoyalty - The 5-Band Platform Architecture" />
              </div>
            </div>
          </div>
        </section>
        
        {/* Platform Bands */}
        <section className="section">
          <div className="container max-w-4xl">
            {bands.map((band, index) => (
              <BandCard key={index} {...band} />
            ))}
          </div>
        </section>
        
        {/* CTA */}
        <section className="cta-section">
          <div className="container relative z-10">
            <h2>Ready to See the Platform in Action?</h2>
            <p>Request an enterprise demo to see how ChannelLoyalty transforms channel loyalty.</p>
            <div className="cta-buttons">
              <button onClick={() => setDemoModalOpen(true)} className="btn btn-primary">
                Request Demo <ArrowRight size={18} />
              </button>
              <a href="https://wa.me/919910059861" target="_blank" rel="noopener noreferrer" className="btn btn-ghost">
                <MessageCircle size={18} /> Talk to Expert
              </a>
            </div>
          </div>
        </section>
      </main>

      <MegaFooter />
      <WhatsAppFloat />
      <ChatWidget />
      <DemoModal isOpen={demoModalOpen} onClose={() => setDemoModalOpen(false)} />
    </div>
  );
}
